import type { ComponentProps } from "react";
import { Link } from "react-router";

import { cn } from "@/utils/tailwind";

// Combine a bookmark ribbon and stacked page into a provider-independent mark.
//
export function LogoMark({ className, ...props }: ComponentProps<"svg">) {
  return (
    <svg
      aria-hidden="true"
      className={cn("size-9", className)}
      fill="none"
      viewBox="0 0 42 42"
      xmlns="http://www.w3.org/2000/svg"
      {...props}
    >
      <rect className="fill-foreground" height="42" rx="14" width="42" />
      <path
        className="fill-background"
        d="M11.5 10.5h19v20h-19z"
        opacity=".92"
      />
      <path
        className="stroke-foreground"
        d="M15 16h12M15 21h9"
        strokeLinecap="round"
        strokeWidth="1.8"
      />
      <path className="fill-signal" d="M23 8.5h7v17l-3.5-2.8-3.5 2.8z" />
    </svg>
  );
}

interface LogoLockupProps extends Omit<ComponentProps<typeof Link>, "to"> {
  linkTo?: string;
}

// Pair the product mark with its complete domain-name wordmark.
//
export function LogoLockup({ className, linkTo, ...props }: LogoLockupProps) {
  const content = (
    <>
      <LogoMark />
      <span className="inline-flex items-baseline gap-1.5">
        <span className="font-display text-[1.3rem] font-semibold tracking-[-0.04em]">
          bookmarked
        </span>
        <span className="text-signal-strong text-[0.6rem] font-bold tracking-[0.16em] uppercase">
          FYI
        </span>
      </span>
    </>
  );
  const classes = cn("inline-flex items-center gap-2.5", className);

  return linkTo ? (
    <Link className={classes} to={linkTo} {...props}>
      {content}
    </Link>
  ) : (
    <div className={classes}>{content}</div>
  );
}
